import { useEffect, useLayoutEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../auth/store";
import { usePortalStore } from "../portal/store";
import { useUiStore } from "../ui/theme";
import { type PrincipalLook, cabinetOfPath, decideLook, lookSignature, readCachedLook, useLookStore, writeCachedLook } from "./look";
import { useWorkspace } from "./useWorkspace";

const OPTION = "use_personal_workspace";

function staffPrincipal(accessToken: string | null, me: ReturnType<typeof useAuthStore.getState>["me"]): PrincipalLook {
  return { id: me?.user.id ?? null, signedIn: Boolean(accessToken && me), option: Boolean(me?.feature_options?.[OPTION]) };
}

function portalPrincipal(accessToken: string | null, me: ReturnType<typeof usePortalStore.getState>["me"]): PrincipalLook {
  return {
    id: me?.user.id ?? null,
    signedIn: Boolean(accessToken && me),
    option: Boolean(me?.feature_options?.[OPTION]),
    locked: Boolean(me?.must_change_password),
  };
}

/**
 * UXW-17: the look of the first paint — from the scoped cache of the principal of the cabinet in the address, or the
 * standard of that cabinet; called once before the first render, so neither another person's look nor a grey frame
 * before the lilac one flashes.
 */
export function primeLook(pathname: string = window.location.pathname): void {
  const auth = useAuthStore.getState();
  const client = usePortalStore.getState();
  const staff = staffPrincipal(auth.accessToken, auth.me);
  const portal = portalPrincipal(client.accessToken, client.me);
  const cabinet = cabinetOfPath(pathname);
  const who = cabinet === "portal" ? portal : staff;
  const cached = cabinet && !who.locked ? readCachedLook(cabinet, who.id) : null;
  const theme = useUiStore.getState().theme;
  useLookStore.getState().setActive(decideLook({ pathname, theme, staff, portal, saved: cached?.appearance, palette: cached?.palette }));
}

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-10, CW-09): keeps the look of the root theme in step with the address, the
 * sessions, the option and the theme. Lives inside the router (the root theme is outside it) and renders nothing.
 */
export function PersonalLookSync() {
  const { pathname } = useLocation();
  const theme = useUiStore((s) => s.theme);
  const staffToken = useAuthStore((s) => s.accessToken);
  const staffMe = useAuthStore((s) => s.me);
  const portalToken = usePortalStore((s) => s.accessToken);
  const portalMe = usePortalStore((s) => s.me);
  const staff = useMemo(() => staffPrincipal(staffToken, staffMe), [staffToken, staffMe]);
  const portal = useMemo(() => portalPrincipal(portalToken, portalMe), [portalToken, portalMe]);
  const cabinet = cabinetOfPath(pathname);
  const who = cabinet === "portal" ? portal : staff;
  // the saved look is asked only when it can be shown: light theme, option on, the cabinet open to this principal
  const wanted = Boolean(cabinet && who.signedIn && who.option && !who.locked && theme === "light");
  const workspace = useWorkspace(cabinet ?? "staff", wanted);
  const data = wanted && workspace.data?.cabinet === cabinet ? workspace.data : undefined;
  const principal = who.id;
  useEffect(() => {
    if (data && cabinet && principal) writeCachedLook(cabinet, principal, data);
  }, [data, cabinet, principal]);
  const cached = useMemo(() => (cabinet && wanted && !data ? readCachedLook(cabinet, principal) : null), [cabinet, wanted, data, principal]);
  const preview = useLookStore((s) => s.preview);
  const look = decideLook({
    pathname,
    theme,
    staff,
    portal,
    saved: data?.workspace.appearance ?? cached?.appearance,
    palette: data?.palette ?? cached?.palette,
    preview,
  });
  const signature = `${lookSignature(look)}|${look?.previewing ? "preview" : "saved"}`;
  // before the paint: the root theme changes in the same frame as the address or the choice
  useLayoutEffect(() => {
    useLookStore.getState().setActive(look);
    // the signature names everything the look depends on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return null;
}
